import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'user' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole = 'user' }) => {
  const { isAuthenticated, user, isInitialized, isLoading } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // Show loading spinner while initializing auth
  if (!isInitialized || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated (only after initialization is complete)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has the required role
  if (requiredRole) {
    // Admin routes require exact admin role
    if (requiredRole === 'admin' && user?.role !== 'admin') {
      return <Navigate to="/unauthorized" replace />;
    }

    // User routes should be accessible to both users and admins
    if (requiredRole === 'user' && !(user?.role === 'user' || user?.role === 'admin')) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check if user is approved (temporarily disabled for testing)
  // if (user?.registrationStatus !== 'approved') {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '100vh',
  //         flexDirection: 'column',
  //       }}
  //     >
  //       <CircularProgress />
  //       <Box sx={{ mt: 2 }}>
  //         <h2>Account Pending Approval</h2>
  //         <p>Your account is currently pending approval by an administrator.</p>
  //         <p>You will be notified once your account has been approved.</p>
  //       </Box>
  //     </Box>
  //   );
  // }

  return <>{children}</>;
};

export default ProtectedRoute;